<script setup lang="ts" generic="T extends SelectInputType = string">
import {
  ref,
  toRef,
  watch,
} from 'vue';

import type { SelectInputType } from '@modules/forms/types';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VHorizontalScrollNav from '@components/scroll/VHorizontalScrollNav/VHorizontalScrollNav.vue';
import VFormInputWrapper
  from '@modules/forms/components/inputs/VFormInputWrapper/VFormInputWrapper.vue';
import { watchImmediate } from '@vueuse/core';

import type {
  VFormSelectButtonInputEmits,
  VFormSelectButtonInputProps,
} from './types';
import { useInputInnerValue } from '../composables';

const props = withDefaults(defineProps<VFormSelectButtonInputProps<T>>(), {});
const emit = defineEmits<VFormSelectButtonInputEmits<T>>();

const { innerValue } = useInputInnerValue(toRef(props, 'value'));

const activeIndex = ref<number>();

function syncActiveIndex(): void {
  if (!props.options?.length) {
    activeIndex.value = undefined;
    return;
  }

  const index = props.options.findIndex((a) => a.value === innerValue.value);

  activeIndex.value = Math.max(index, 0);
}

function onDeactivate(ev: MouseEvent, isActive: boolean): void {
  if (!props.unselectable && isActive) {
    ev.stopPropagation();
  }
}

watchImmediate(() => props.options, () => {
  syncActiveIndex();
});

watch(innerValue, (newValue, oldValue) => {
  if (props.required && !newValue && oldValue) {
    innerValue.value = oldValue;
    return;
  }

  if (!props.readonly) {
    syncActiveIndex();
    emit('change', innerValue.value);
  }
});
</script>

<template>
  <VFormInputWrapper v-data-qa-auto-id="'VFormSelectButtonInput'"
    :errors="errors"
    :hide-errors="hideErrors"
    :readonly="readonly"
    class="relative"
  >
    <label
      v-if="label"
      class="mb-2 block text-muted-color"
    >
      {{ label }}
    </label>
    <VHorizontalScrollNav
      :active-index="activeIndex"
      element-class="p-button"
    >
      <PSelectButton
        v-model="innerValue"
        :name="name"
        :input-id="name"
        :required="required"
        :disabled="disabled"
        :options="options"
        option-label="label"
        option-value="value"
        class="flex w-full"
        :class="{ 'p-invalid': isInvalid && !readonly }"
        :autocomplete="autocomplete ? autocomplete : 'off'"
        aria-labelledby="basic"
      >
        <template #option="{ option }">
          <div
            data-id="select-button-item"
            :data-value="option.value"
            class="flex min-h-[28px] w-full items-center justify-center gap-4"
            @click="onDeactivate($event, option.value === innerValue)"
          >
            <span class="whitespace-nowrap">{{ option.label }}</span>
            <VIconButton
              v-if="option.showDeleteButton"
              icon="pi-trash"
              size="small"
              @click.stop="emit('delete', option.value)"
            />
          </div>
        </template>
      </PSelectButton>
    </VHorizontalScrollNav>

    <div
      v-if="readonly"
      data-id="select-button-readonly-overlay"
      class="absolute inset-0"
    />
  </VFormInputWrapper>
</template>
